/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { COMPANY_ACCESS_ROLES, COMPANY_TYPE, USER_TYPE } from '../../../utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { addNewUser, fetchUserOrders, fetchUserQuotes, listUsers, updateActiveUser, updateUserDetails } from '../../../store/actions/users';
import { listCompanies, listSellersUsers } from '../../../store/actions/companies';
import Datagrid from '../../ui/Datagrid';
import { renderCell } from '../../ui/Datagrid/table_config';
import columns from './table_config';
import { useAuth } from '../../../hooks/useAuth';
import { Box, Grid, IconButton, Switch } from '@mui/material';
import UserCartButton from '../../ui/CartButton';
import UsersOrders from '../../usersOrders';
import UsersQuotes from './../../usersQuotes/index';
import { BarChart, Favorite, Person, PlaylistAddCheck, SaveRounded, SellRounded, WidgetsRounded, AdminPanelSettings, Map } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import SelectEditCompanyCell from '../../ui/SelectEditCompanyCell';
import { enqueueSnackbar } from '../../../store/actions/snackbar';
import { validateEmail } from '../../../utils/validateEmail';
import SellersModalDialog from '../../ui/SellersModalDialog';
import SelectEditInputTextField from '../../ui/SelectEditInputTextField';
import SelectEditValueTypes from '../../ui/SelectEditValueTypes';
import AccessLevelsDialog from '../../ui/AccessLevelsDialog';
import { containsAnyKeys } from '../../../utils/objectHelpers';
import SpecialInstructionsDialog from '../../ui/SpecialInstructionsDialog';
import PersonnelDialog from '../../ui/PersonnelDialog';
import SpendChartDialog from '../../ui/SpendChartDialog';
import FavoritePartDialog from '../../ui/FavoritePartDialog';
import { blue } from '@mui/material/colors';
import store from '../../../store/store';
import { REDUX_ACTIONS } from '../../../shared/config/constants';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ManageDivisionsDialog from '../../ui/ManageDivisionsDialog';

const hideCompaniesList = [USER_TYPE.CUSTOMER_ADMIN, USER_TYPE.CUSTOMER_MANAGER, USER_TYPE.SELLER_MARKETPLACE_ADMIN];

const Users = () => {
  const [emailEditable, setEmailEditable] = useState(false);
  const users = useSelector((state) => state.users);

  const { user } = useAuth();

  const orderPartsPostSuccess = useSelector((state) => state.orders?.orderPartsPostSuccess);

  const quotePartsPostSuccess = useSelector((state) => state.quotes?.quotePartsPostSuccess);

  const companies = useSelector((state) => state.companies);

  const dispatch = useDispatch();

  const apiRef = useGridApiRef();

  const [isSellerDialogOpen, setIsSellerDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState();
  const [gridColumns, setGridColumns] = useState([]);
  const [properties, setProperties] = useState([]);
  const {
    unSavedExists
  } = useUnsavedChanges(properties);

  const [specialInstructionDialogOpen, setSpecialInstructionDialogOpen] = useState({
    open: false,
    data: null
  });

  const [personnelDialogOpen, setPersonnelDialogOpen] = useState({
    open: false,
    data: null
  });

  const [accesLevelsDialog, setAccesLevelsDialog] = useState({
    open: false,
    data: null
  });

  const [spendChartDialogOpen, setSpendChartDialogOpen] = useState({
    open: false,
    data: null
  });

  const [favoritePartsDialogOpen, setFavoritePartsDialogOpen] = useState({
    open: false,
    data: null
  });

  const [manageDivisionDialog, setManageDivisionDialog] = useState({
    open: false,
    data: null
  });

  useEffect(() => {
    dispatch({
      type: REDUX_ACTIONS.USERS_ADD_DISABLE,
      payload: unSavedExists
    });
  }, [unSavedExists]);

  useEffect(() => {
    if (!users.loading) {
      if (user.type === USER_TYPE.SELLER_MARKETPLACE_ADMIN) {
        dispatch(listSellersUsers(user));
      } else {
        dispatch(listUsers(user));
      }
    }
  }, []);

  useEffect(() => {
    if (users?.data) {
      setProperties(users?.data);
    }
  }, [users.data]);

  useEffect(() => {
    if (!users?.loading && users?.data?.length > 0) {
      let columnsList = columns(renderCell, renderOrdersCell, renderQuotesCell, user, renderActions, renderCompaniesCell, renderAccessLevelCell, emailEditable, renderSellersCell, renderInventoryCell, renderValueCell, renderSpecialInstructionsCell, renderPersonnelCell, renderAccessLevelsCell, renderSpendCell, renderFavoritesCell, renderUserActiveCell);
      columnsList = columnsList.filter(obj => !obj.hide);
      setGridColumns(columnsList);
      apiRef.current.updateColumns(columnsList);
    }
  }, [users.data, emailEditable]);

  useEffect(() => {
    if (orderPartsPostSuccess && !users.loading) {
      dispatch(listUsers(user));
    }
  }, [orderPartsPostSuccess]);

  useEffect(() => {
    if (quotePartsPostSuccess && !users.loading) {
      dispatch(listUsers(user));
    }
  }, [quotePartsPostSuccess]);

  useEffect(() => {
    if (hideCompaniesList.includes(user.type)) return;
    if (companies.data.length === 0) {
      dispatch(listCompanies(user));
    }
  }, []);

  useEffect(() => {
    if (properties[0]?.firstName === '') {
      const id = properties[0]?._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 1
        });
        apiRef.current.setCellMode(id, 'firstName', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'firstName');
        }, 50);
      }, 50);
    }
  }, [properties?.length]);

  const renderOrdersCell = (_props) => {
    const orders = _props.row.ordersCount;
    const emailAddress = _props.row.emailAddress;
    return (
      <>
        {orders > 0 && (
          <UserCartButton
            data={orders}
            userId={_props.row._id}
            type='orders'
            fetchAction={fetchUserOrders}
            modalComponent={
              <UsersOrders
                userOrder={_props.row}
                title={emailAddress}
                companyId={_props.row.companyId}
                userId={_props.row._id}
              />
            }
            cartColor="success"
          />
        )}
      </>
    );
  };
  const renderQuotesCell = (_props) => {
    const quotes = _props.row.quotesCount;
    const emailAddress = _props.row.emailAddress;
    return (
      <>
        {quotes > 0 && (
          <UserCartButton
            data={quotes}
            userId={_props.row._id}
            type='quotes'
            fetchAction={fetchUserQuotes}
            modalComponent={
              <UsersQuotes
                userQuote={_props.row}
                title={emailAddress}
                companyId={_props.row.companyId}
              />
            }
            cartColor="rgba(0, 0, 0, 0.2)"
          />
        )}
      </>
    );
  };

  const checkIsValid = (data) => {
    if (!data?.firstName) {
      dispatch(
        enqueueSnackbar(
          'Please enter valid name',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    if (!data?.emailAddress || !validateEmail(data?.emailAddress)) {
      dispatch(
        enqueueSnackbar(
          'Please enter valid email address',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    if (!data?.companyName) {
      dispatch(
        enqueueSnackbar(
          'Please select a company',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    if (!data?.type) {
      dispatch(
        enqueueSnackbar(
          'Please select access level',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    return true;
  };

  const handleSave = (row) => {
    if (checkIsValid(row)) {
      const body = {
        userDetails: {
          companyId: companies?.data.filter((item) => item.name === row?.companyName)[0]._id,
          type: row?.type,
          firstName: row?.firstName,
          emailAddress: row?.emailAddress,
          accessLevels: row?.accessLevels,
          phone: row?.phone,
          orderApprovers: row?.orderApprovers,
          quoteApprovers: row?.quoteApprovers,
          admin: row?.admin,
          specialInstructions: row?.specialInstructions,
          active: row?.active,
          managingDivision: row?.managingDivision
        },
      };

      if (!row?.isNewRow) {
        body.userId = row?._id;
        dispatch(updateUserDetails(body));
      } else {
        dispatch(addNewUser(body));
      }
    }
  };

  const renderActions = ({ row }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
        disabled={(row.type === USER_TYPE.PREMIUM_SELECT_ADMIN && !row?.accessLevels?.length) || (!row?.isNewRow && !row.isDirty) || users.loading}
        variant="solid"
        onClick={() => {
          handleSave(row);
        }}
      >
        <SaveRounded />
      </IconButton>
      </Grid>
    );
  };

  const renderSpecialInstructionsCell = (props) => {
    return (
      <Grid
        container
        justifyContent="center"
        style={
          props.row.type === 'CUSTOMER_RIG' && !props?.row?.isNewRow
            ? { visibility: 'visible' }
            : { visibility: 'hidden' }
        }
      >
        <IconButton
          variant="solid"
          sx={{ color: !_.isEmpty(props.row?.specialInstructions) && 'green' }}
          onClick={(_evt) => {
            setSpecialInstructionDialogOpen({
              open: true,
              data: props.row
            });
          }}
        >
          <PlaylistAddCheck fontSize='large' />
        </IconButton>
      </Grid>
    );
  };

  const renderPersonnelCell = (props) => {
    return (
      <Grid
        container
        justifyContent="center"
        style={
          props.row.type === 'CUSTOMER_RIG' && !props?.row?.isNewRow
            ? { visibility: 'visible' }
            : { visibility: 'hidden' }
        }
      >
        <IconButton
          variant="solid"
          sx={{ color: !_.isEmpty(props.row?.orderApprovers || props.row?.quoteApprovers || props.row?.admins) && 'green' }}
          onClick={(_evt) => {
            setPersonnelDialogOpen({
              open: true,
              data: props.row
            });
          }}
        >
          <Person fontSize='large' />
        </IconButton>
      </Grid>
    );
  };

  const renderSpendCell = (props) => {
    return (
      <Grid
        container
        justifyContent="center"
        style={
          (props.row.type === 'CUSTOMER_RIG' || props.row.type === 'CUSTOMER_USER') && !props?.row?.isNewRow
            ? { visibility: 'visible' }
            : { visibility: 'hidden' }
        }
      >
        <IconButton
          variant="solid"
          onClick={(_evt) => {
            setSpendChartDialogOpen({
              open: true,
              data: props.row
            });
          }}
        >
          <BarChart fontSize='large' />
        </IconButton>
      </Grid>
    );
  };

  const renderFavoritesCell = (props) => {
    return (
      <Grid
        container
        justifyContent="center"
        style={
          props?.row?.isPartsFavorites && (props.row.type === 'CUSTOMER_RIG' || props.row.type === 'CUSTOMER_USER') && !props?.row?.isNewRow
            ? { visibility: 'visible' }
            : { visibility: 'hidden' }
        }
      >
        <IconButton
          variant="solid"
          onClick={(_evt) => {
            setFavoritePartsDialogOpen({
              open: true,
              data: props.row
            });
          }}
        >
          <Favorite/>
        </IconButton>
      </Grid>
    );
  };

  const updateData = (id, field, value) => {
    setProperties((prevProperties) => {
      return prevProperties.map((item) => {
        if (item._id === id) {
          const isDirty = value !== item[field];

          const updatedItem = {
            ...item,
            [field]: value,
            isDirty: item.isDirty || isDirty,
          };

          if (field === 'companyName') {
            updatedItem.type = '';
          }

          return updatedItem;
        }
        return item;
      });
    });
  };

  const updateActiveUserRow = (row, value) => {
    const { users } = store.getState();
    const updatedProperties = users.data.map(item => {
      if (item._id === row?._id) {
        return {
          ...item,
          active: value
        };
      }
      return item;
    });
    dispatch({
      type: REDUX_ACTIONS.USERS_LIST_SUCCESS,
      payload: {
        users: updatedProperties,
        paginatedElements: updatedProperties.length,
      },
    });
  };

  const renderUserActiveCell = ({ row }) => {
    return (
    <>
      <Grid container justifyContent="center">
     <Box>
         <Switch
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: blue[300],
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: blue[300],
            },
          }}
          checked={row?.active}
          onChange={(event) => {
            if (row?.isNewRow) {
              updateActiveUserRow(row, event.target.checked);
            } else {
              updateActiveUserRow(row, event.target.checked);
              const body = {
                active: event.target.checked,
                _id: row?._id
              };
              dispatch(updateActiveUser(body));
            }
          }}
          inputProps={{ 'aria-label': 'controlled' }}
          color="default"/>
         </Box>
      </Grid>
    </>
    );
  };

  const renderAccessLevelCell = (params) => {
    const isEditable = !!params.row.companyName;
    const companyType = companies?.data.find((item) => item?.name === params?.row?.companyName)?.type;

    const getAccessLevelData = () => {
      switch (companyType) {
        case COMPANY_TYPE.PREMIUM: {
          const { type, accessLevels } = user;

          const isSelectAdmin = type === USER_TYPE.PREMIUM_SELECT_ADMIN;
          const hasRealtimeAdminAccess =
            type === USER_TYPE.PREMIUM_REALTIME_MPD_ADMIN ||
            (isSelectAdmin && containsAnyKeys([USER_TYPE.PREMIUM_REALTIME_MPD_ADMIN], accessLevels));

          const hasFieldTicketingAdminAccess =
            type === USER_TYPE.PREMIUM_FIELD_TICKETING_ADMIN ||
            (isSelectAdmin && containsAnyKeys([USER_TYPE.PREMIUM_FIELD_TICKETING_ADMIN], accessLevels));

          if (hasRealtimeAdminAccess && hasFieldTicketingAdminAccess) {
            return [
              USER_TYPE.PREMIUM_MPD_USER,
              USER_TYPE.PREMIUM_MPD_MANAGER,
              USER_TYPE.PREMIUM_FIELD_TICKETING_MANAGER,
              USER_TYPE.PREMIUM_FIELD_TICKETING_USER
            ];
          }

          if (hasRealtimeAdminAccess) {
            return [USER_TYPE.PREMIUM_MPD_USER, USER_TYPE.PREMIUM_MPD_MANAGER];
          }

          if (hasFieldTicketingAdminAccess) {
            return [
              USER_TYPE.PREMIUM_FIELD_TICKETING_MANAGER,
              USER_TYPE.PREMIUM_FIELD_TICKETING_USER
            ];
          }

          return COMPANY_ACCESS_ROLES.PREMIUM;
        }

        case COMPANY_TYPE.MPD_CUSTOMER:
          return COMPANY_ACCESS_ROLES.MPD_CUSTOMER;

        case COMPANY_TYPE.MARKETPLACE_CUSTOMER:
          return COMPANY_ACCESS_ROLES.MARKETPLACE_CUSTOMER;

        case COMPANY_TYPE.SELLER:
          return COMPANY_ACCESS_ROLES.SELLER;

        default:
          return [];
      }
    };

    const accessLevelData = getAccessLevelData();

    // eslint-disable-next-line multiline-ternary
    return isEditable ? (
      <SelectEditValueTypes
        {...params}
        closeCellAfterSelect={true}
        data={accessLevelData}
        updateData={updateData}
      />
    ) : null;
  };

  const renderCompaniesCell = (params) => {
    let filteredCompanies = companies?.data;

    if (user.type === USER_TYPE.PREMIUM_REALTIME_MPD_ADMIN) {
      filteredCompanies = companies?.data?.filter(
        (company) => company.type === COMPANY_TYPE.MPD_CUSTOMER || company.type === COMPANY_TYPE.PREMIUM
      );
    }
    return <SelectEditCompanyCell {...params} closeCellAfterSelect={true} data={filteredCompanies} updateData={updateData} />;
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField {...params} updateData={updateData} />;
  };

  const showSellersDialog = (user) => {
    setCurrentUser(user);
    setIsSellerDialogOpen(true);
  };

  const renderSellersCell = (params) => {
    return (
      <Grid
        container
        justifyContent="center"
        style={
          params.row.type === USER_TYPE.CUSTOMER_RIG && !params?.row?.isNewRow
            ? { visibility: 'visible' }
            : { visibility: 'hidden' }
        }
      >
        <IconButton
          variant="solid"
          onClick={() => {
            showSellersDialog(params.row);
          }}
        >
          <SellRounded />
        </IconButton>
      </Grid>
    );
  };

  const renderAccessLevelsCell = (params) => {
    return (
      <Grid
        container
        justifyContent="center"
      >
        <IconButton
          variant="solid"
          sx={{
            color: !_.isEmpty(params.row?.accessLevels) && 'green',
            display: params.row.type === USER_TYPE.PREMIUM_SELECT_ADMIN ? '' : 'none',
          }}
          onClick={(_evt) => {
            setAccesLevelsDialog({
              open: true,
              data: params.row
            });
          }}
        >
          <AdminPanelSettings />
        </IconButton>
        <IconButton
          variant="solid"
          sx={{
            color: !_.isEmpty(params.row?.managingDivision) && 'green',
            display: (params.row.type === USER_TYPE.PREMIUM_FIELD_TICKETING_MANAGER || (params.row.type === USER_TYPE.PREMIUM_SELECT_ADMIN && params.row?.accessLevels?.includes(USER_TYPE.PREMIUM_FIELD_TICKETING_MANAGER))) && !params?.row?.isNewRow ? '' : 'none',
          }}
          onClick={(_evt) => {
            setManageDivisionDialog({
              open: true,
              data: params.row
            });
          }}
        >
          <Map />
        </IconButton>
      </Grid>
    );
  };

  const renderInventoryCell = (params) => {
    return (
      <Grid
        container
        justifyContent="center"
        style={((user.type === USER_TYPE.PREMIUM_ADMIN || user.type === USER_TYPE.PREMIUM_MARKETPLACE_ADMIN || (user.type === USER_TYPE.PREMIUM_SELECT_ADMIN &&
          containsAnyKeys([USER_TYPE.PREMIUM_ADMIN, USER_TYPE.PREMIUM_MARKETPLACE_ADMIN], user?.accessLevels || [])) ||
           user.type === USER_TYPE.SELLER_MARKETPLACE_ADMIN || user.type === USER_TYPE.CUSTOMER_MANAGER || user.type === USER_TYPE.CUSTOMER_ADMIN) &&
           params.row.type === USER_TYPE.CUSTOMER_RIG)
          ? { visibility: 'visible' }
          : { visibility: 'hidden' }
        }
      >
        <IconButton
          variant="solid"
          onClick={(_evt) => {
            let url;
            if (user.type === USER_TYPE.PREMIUM_MARKETPLACE_ADMIN || (user.type === USER_TYPE.PREMIUM_SELECT_ADMIN &&
              containsAnyKeys([USER_TYPE.PREMIUM_ADMIN, USER_TYPE.PREMIUM_MARKETPLACE_ADMIN], user?.accessLevels || [])) || user.type === USER_TYPE.SELLER_MARKETPLACE_ADMIN || user.type === USER_TYPE.PREMIUM_ADMIN) {
              url = `/Shop?companyId=${params.row._id}&vendorId=${user.companyId}&name=${params.row.firstName}`;
            }
            if (user.type === USER_TYPE.CUSTOMER_MANAGER || user.type === USER_TYPE.CUSTOMER_ADMIN) {
              url = `/Shop?companyId=${params.row._id}&name=${params.row.firstName}&customerRigUser=${params.row._id}`;
            }
            window.open(url, '_blank');
          }}
        >
          <WidgetsRounded />
        </IconButton>
      </Grid>
    );
  };

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12}>
        <Datagrid
          apiRef={apiRef}
          data={properties}
          autoHeight={false}
          columns={gridColumns}
          pageSize={100}
          disableStickyHeader={false}
          loading={users.loading}
          autoheight
          editMode="cell"
          height={'90vh'}
          toolbar={{
            options: {
              columns: true,
              filters: true,
              density: true,
              export: false,
            },
          }}
          onRowSelectionModelChange={(ids) => {
            const isEmailEditable = apiRef?.current.getSelectedRows().get(ids[0])?.isNewRow || false;
            setEmailEditable(isEmailEditable);
          }}
        />
      </Grid>
      {isSellerDialogOpen && (
        <SellersModalDialog
          isOpen={isSellerDialogOpen}
          onActionClick={() => { }}
          onCloseClick={() => {
            setIsSellerDialogOpen(false);
          }}
          selectedUser={currentUser}
        />
      )}
      {specialInstructionDialogOpen.open && (
        <SpecialInstructionsDialog
          isOpen={specialInstructionDialogOpen.open}
          data={specialInstructionDialogOpen.data}
          onCloseClick={() => {
            setSpecialInstructionDialogOpen({
              open: false,
              data: {}
            });
          }}
          handleSave={handleSave}
        />
      )}
       {personnelDialogOpen.open && (
        <PersonnelDialog
          open={personnelDialogOpen.open}
          handleClose={() => {
            setPersonnelDialogOpen({
              open: false,
              data: {}
            });
          }}
          rowData={personnelDialogOpen.data}
          handleSave={handleSave}
        />
       )}
       {manageDivisionDialog.open && (
        <ManageDivisionsDialog
          open={manageDivisionDialog.open}
          handleClose={() => {
            setManageDivisionDialog({
              open: false,
              data: {}
            });
          }}
          rowData={manageDivisionDialog.data}
          handleSave={handleSave}
        />
       )}
       {accesLevelsDialog.open && (
        <AccessLevelsDialog
          open={accesLevelsDialog.open}
          handleClose={() => {
            setAccesLevelsDialog({
              open: false,
              data: {}
            });
          }}
          rowData={accesLevelsDialog.data}
          handleSave={handleSave}
        />
       )}
         {spendChartDialogOpen.open && (
        <SpendChartDialog
          open={spendChartDialogOpen.open}
          handleClose={() => {
            setSpendChartDialogOpen({
              open: false,
              data: {}
            });
          }}
          rowData={spendChartDialogOpen.data}
          type='user'
        />
         )}
         {favoritePartsDialogOpen.open && (
        <FavoritePartDialog
          open={favoritePartsDialogOpen.open}
          handleClose={() => {
            setFavoritePartsDialogOpen({
              open: false,
              data: {}
            });
          }}
          rowData={favoritePartsDialogOpen.data}
        />
         )}
    </Grid>
  );
};

export default Users;
